import clsx from 'clsx';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { BackSection } from 'src/components/common/BackSection';
import { snapshotPickerWrapper } from 'src/components/FilterToolbarEmbedded/FilterList/FiltersList.css';
import { ReportExportOptionsV2 } from 'src/components/Reports/ReportsExport/ReportsExport.types';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { useFileExportContext } from 'src/contexts/FileExportContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useReportMetricsContextV2 } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { ReportSnapshotInput } from 'src/core/POS/DateSelector/ReportSnapshotInput';
import { vars } from 'src/core/themes';
import { Button, DropdownMenu, Stack } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { ReportBuilderDialogV2 } from 'src/dialogs/ReportBuilderDialog/ReportBuilderDialogV2';
import { ShareReportDialog } from 'src/dialogs/ShareReportDialog';
import {
  ReportConfigV2,
  useReportConfigActionsV2,
  useReportPermissions,
} from 'src/hooks/reportConfigs';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { getReportNavUrl } from 'src/navigations/Routes/Reports/useReportV2Actions';
import { CopyIcon, DiskSaveIcon } from 'src/svgs';
import { ReportEditTableIcon } from 'src/svgs/ReportEditTableIcon';
import {
  DownloadIcon,
  EditIcon,
  IconsFill,
  ProcessingIcon,
  ShareIcon,
} from 'src/svgs/Viagogo';
import { DataLagIndicator } from 'src/tables/ReportTableCommon/DataLagIndicator';
import { ContentId } from 'src/utils/constants/contentId';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { Feature, Permission, ReportFileType } from 'src/WebApiController';

import {
  darkModeLightActive,
  darkModeLightActiveOutline,
} from '../Reports.css';
import { ReportsExportV2 } from '../ReportsExport/ReportsExportV2';
import * as styles from './ReportsPageToolbar.css';

const ReportTypesSnapshotEnabled = [
  ReportTypesV2.InventoryV2,
  ReportTypesV2.SaleV2,
];

export const ReportsPageToolbarV2 = ({
  reportConfig,
  isEditTable,
  setIsEditTable,
  returnUrl,
}: {
  reportConfig: ReportConfigV2 | undefined;
  isEditTable: boolean;
  setIsEditTable: (isEditTable: boolean) => void;
  returnUrl: string;
}) => {
  const shareDialog = useBasicDialog();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openReportBuilderDialog,
    closeDialog: closeReportBuilderDialog,
  } = useDialog(DialogId.ReportBuilder, ReportBuilderDialogV2);
  const { filterQuery } = useFilterQueryContext<QueryWithViewMode>();

  const { canUpdateReport } = useReportPermissions(reportConfig);
  const hasReportingV2Feature = useUserHasFeature(Feature.ReportingV2);
  const hasCreateReportFeature = useUserHasAnyOfPermissions(
    Permission.Reports_CreateReports
  );

  const { isReportFilterDirty, isReportDirty, originalReportConfig } =
    useReportMetricsContextV2();

  const { fileExportIsPending, fileExportProgressFormatted } =
    useFileExportContext();

  const shareText = useContent(ContentId.Share);
  const duplicateText = useContent(ContentId.Duplicate);
  const downloadText = useContent(ContentId.Download);

  const [reportExportOptions, setReportExportOptions] =
    useState<ReportExportOptionsV2 | null>(null);
  const [snapshotDate, setSnapshotDate] = useState<Date | undefined>(undefined);

  const onReportExported = () => {
    setReportExportOptions(null);
  };

  const reportConfigWithTempQuery = useMemo(() => {
    if (!reportConfig) {
      return reportConfig;
    }
    return {
      ...reportConfig,
      request: {
        ...reportConfig.request,
        filters: filterQuery,
      },
    } as ReportConfigV2;
  }, [filterQuery, reportConfig]);

  const { upsertReportConfig, getReportConfigNameForDuplicate } =
    useReportConfigActionsV2();

  const invokeUpsertReport = useCallback(
    async (report: ReportConfigV2) => {
      return await tryInvokeApi(
        async () => {
          return await upsertReportConfig(report);
        },
        (error) => {
          showErrorDialog('ReportClient.mergeReport', error, {
            trackErrorData: report,
          });
        }
      );
    },
    [showErrorDialog, upsertReportConfig]
  );

  const onReportShareSubmit = useCallback<
    ComponentProps<typeof ShareReportDialog>['onOkay']
  >(
    async (roleIds, sellerUserIds) => {
      const updatedReport = {
        ...reportConfig!,
        roleIdsToShare: roleIds,
        sellerUserIdsToShare: sellerUserIds,
      };
      await invokeUpsertReport(updatedReport);
      shareDialog.closeDialog();
    },
    [reportConfig, invokeUpsertReport, shareDialog]
  );

  const onReportDuplicateSubmit = useCallback(async () => {
    const newReport = {
      ...reportConfigWithTempQuery!,
      reportId: null,
      reportName: getReportConfigNameForDuplicate(
        reportConfig!.reportType,
        reportConfig!.reportName
      ),
    } as ReportConfigV2;
    const newReportId = await invokeUpsertReport(newReport);
    if (newReportId) {
      window.open(
        getReportNavUrl(reportConfig!.reportType, newReportId),
        '_blank',
        'noreferrer'
      );
    }
  }, [
    reportConfigWithTempQuery,
    getReportConfigNameForDuplicate,
    reportConfig,
    invokeUpsertReport,
  ]);

  const onDownloadSnapshot = async () => {
    setReportExportOptions({
      reportToExport: reportConfigWithTempQuery!,
      fileType: ReportFileType.CSV,
      snapshotDate, // only set this when downloading snapshot
    });
  };

  const onEditReportClick = useCallback(() => {
    if (!originalReportConfig) return;
    openReportBuilderDialog({
      report: originalReportConfig,
      onSave: async (data) => {
        await invokeUpsertReport(data);
        closeReportBuilderDialog();
      },
      editNameOnly: true,
      onClosed: closeReportBuilderDialog,
      onCancel: closeReportBuilderDialog,
    });
  }, [
    originalReportConfig,
    openReportBuilderDialog,
    invokeUpsertReport,
    closeReportBuilderDialog,
  ]);

  return (
    <>
      <Stack gap="s" direction="column">
        <BackSection returnUrl={returnUrl} />
        <div className={styles.toolbar}>
          <Stack direction="row" alignItems="center" gap="m">
            {reportConfig?.reportName ? (
              <h1 className={styles.pageName}>{reportConfig.reportName}</h1>
            ) : null}
            {canUpdateReport && originalReportConfig && (
              <EditIcon
                onClick={onEditReportClick}
                size={vars.iconSize.s}
                withHoverEffect
                darkModeLightHover
                fill={vars.color.textBrandV2}
              />
            )}
            {reportConfig != null && (
              <DataLagIndicator
                lastRefreshTime={reportConfig.lastRefreshTime}
              />
            )}
          </Stack>
          <Stack gap="s">
            {Boolean(
              !isEditTable &&
                (canUpdateReport || reportConfig?.globalReportTypeId != null)
            ) && (
              <ButtonWithIcon
                variant="outline"
                shape="rect"
                className={clsx(
                  styles.transparentButton,
                  darkModeLightActive,
                  darkModeLightActiveOutline
                )}
                icon={
                  <ReportEditTableIcon
                    size={vars.iconSize.s}
                    darkModeLightHover
                    fill={vars.color.textBrand}
                  />
                }
                textContentId={ContentId.EditTable}
                onClick={() => setIsEditTable(true)}
              />
            )}
            {reportConfig &&
              ReportTypesSnapshotEnabled.includes(reportConfig.reportType) && (
                <Stack
                  className={clsx(
                    snapshotPickerWrapper,
                    darkModeLightActiveOutline
                  )}
                >
                  <Stack direction="row" alignItems="center">
                    <ReportSnapshotInput
                      date={snapshotDate}
                      onDateChange={(date: Date) => {
                        setSnapshotDate(date);
                      }}
                      onDownloadSnapshot={onDownloadSnapshot}
                      isSnapshotDownloading={
                        reportExportOptions?.snapshotDate != null ||
                        fileExportIsPending
                      }
                      disabled={
                        reportExportOptions != null || fileExportIsPending
                      }
                    />
                  </Stack>
                </Stack>
              )}
            {reportConfig &&
              hasReportingV2Feature && ( // If they come from V1, we don't allow any actions yet
                <>
                  {(reportExportOptions && !reportExportOptions.snapshotDate) ||
                  fileExportIsPending ? (
                    <Button
                      variant="outline"
                      shape="rect"
                      key="Download"
                      className={clsx(
                        styles.transparentButton,
                        darkModeLightActive,
                        darkModeLightActiveOutline
                      )}
                      title={downloadText}
                      disabled
                    >
                      <RotatingWrapper>
                        <ProcessingIcon
                          fill={IconsFill.textBrand}
                          size={vars.iconSize.l}
                        />
                      </RotatingWrapper>
                      <span style={{ color: vars.color.textBrandV2 }}>
                        {fileExportProgressFormatted}
                      </span>
                    </Button>
                  ) : (
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger
                        asChild
                        disabled={
                          reportExportOptions != null || fileExportIsPending
                        }
                      >
                        <Button
                          variant="outline"
                          shape="rect"
                          key="Download"
                          className={clsx(
                            styles.transparentButton,
                            darkModeLightActiveOutline
                          )}
                          title={downloadText}
                          disabled={
                            reportExportOptions != null || fileExportIsPending
                          }
                        >
                          <DownloadIcon
                            withHoverEffect
                            darkModeLightHover
                            fill={IconsFill.textBrand}
                            size={vars.iconSize.m}
                          />
                        </Button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content align="end">
                          <DropdownMenu.Item
                            key="DownloadCsv"
                            onClick={() => {
                              setReportExportOptions({
                                reportToExport: reportConfigWithTempQuery!,
                                fileType: ReportFileType.CSV,
                                snapshotDate: undefined,
                              });
                            }}
                          >
                            <Content id={ContentId.CommaSeparatedValuesExt} />
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            key="DownloadExcel"
                            onClick={() => {
                              setReportExportOptions({
                                reportToExport: reportConfigWithTempQuery!,
                                fileType: ReportFileType.XLSX,
                                snapshotDate: undefined,
                              });
                            }}
                          >
                            <Content id={ContentId.MicrosoftExcelExt} />
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  )}

                  {reportConfig.isOwner && (
                    <>
                      <Button
                        variant="outline"
                        shape="rect"
                        key="ShareReport"
                        className={clsx(
                          styles.transparentButton,
                          darkModeLightActiveOutline
                        )}
                        title={shareText}
                        onClick={shareDialog.launchDialog}
                      >
                        <ShareIcon
                          withHoverEffect
                          darkModeLightHover
                          fill={IconsFill.textBrand}
                          size={vars.iconSize.m}
                        />
                      </Button>
                    </>
                  )}

                  {hasCreateReportFeature &&
                    reportConfig.reportType != ReportTypesV2.Commission && (
                      <Button
                        variant="outline"
                        shape="rect"
                        className={clsx(
                          styles.transparentButton,
                          darkModeLightActiveOutline
                        )}
                        key="Duplicate"
                        onClick={onReportDuplicateSubmit}
                        title={duplicateText}
                      >
                        <Stack alignItems="center" gap="m" width="full">
                          {reportConfig.globalReportTypeId != null ? (
                            <DiskSaveIcon
                              withHoverEffect
                              darkModeLightHover
                              fill={vars.color.textBrandV2}
                              size={vars.iconSize.l}
                            />
                          ) : (
                            <CopyIcon
                              withHoverEffect
                              darkModeLightHover
                              fill={vars.color.textBrandV2}
                              size={vars.iconSize.l}
                            />
                          )}
                        </Stack>
                      </Button>
                    )}
                </>
              )}
          </Stack>
        </div>
        {reportConfig?.globalReportTypeId != null &&
          reportConfig?.globalReportTypeId !==
            9 /* We don't allow copying commission reports */ && (
            <h2 className={styles.reportCopyMessage}>
              <Content id={ContentId.ReportV2GlobalReportCopyMessageV2} />
            </h2>
          )}
        {canUpdateReport && isReportFilterDirty && (
          <div className={styles.reportCopyMessage}>
            <Content id={ContentId.ReportFilterDirtyWarning} />
          </div>
        )}
        {!canUpdateReport && isReportDirty && (
          <div className={styles.reportCopyMessage}>
            <Content id={ContentId.ReportDirtyWarning} />
          </div>
        )}
      </Stack>
      <ShareReportDialog
        {...shareDialog.dialogProps}
        reportOwnerName={reportConfig?.ownerDisplayName ?? ''}
        reportName={reportConfig?.reportName ?? ''}
        roleIdsShared={reportConfig?.roleIdsToShare ?? []}
        sellerUserIdsShared={reportConfig?.sellerUserIdsToShare ?? []}
        onOkay={onReportShareSubmit}
        onClosed={() => {
          shareDialog.closeDialog();
        }}
        centered
      />
      {reportConfig && (
        <ReportsExportV2
          reportExportOptions={reportExportOptions}
          onReportExported={onReportExported}
        />
      )}
    </>
  );
};
